// The ask for the identity plane (02-DESIGN/03-console/01-the-console.md
// § signing in, § the shape: the workspace menu; hq decision 0072): *All
// workspaces* and *Create a workspace* in the rail's menu go to `/` with the
// ask in the history entry's state, and Choose a workspace then connects
// with `-` as the tenant and lists the memberships, one or many, beside
// *Create a workspace*. Arriving at `/` without the ask keeps today's rule:
// one membership places directly. The ask lives in the entry, so Back and a
// refresh keep it and a fresh visit to `/` does not carry it.

import type { Membership } from "../../wire/identity.ts";

/** KEY names the ask in the history entry's state. */
const KEY = "plane";

function record(state: unknown): Record<string, unknown> | undefined {
  return typeof state === "object" && state !== null ? (state as Record<string, unknown>) : undefined;
}

/** withPlaneAsk is the entry's state with the ask, the rest of it kept — a form the menu opens, for one. */
export function withPlaneAsk(state: unknown): Record<string, unknown> {
  return { ...(record(state) ?? {}), [KEY]: true };
}

/** askedPlane reports whether the entry asked for the identity plane. */
export function askedPlane(state: unknown): boolean {
  return record(state)?.[KEY] === true;
}

/**
 * PLANE_MISSED says why the page cannot list the workspaces when the plane was
 * asked for and the sign-in was placed elsewhere — an auth service older than
 * decision 0072, which reads `-` as no workspace named.
 */
export const PLANE_MISSED =
  "This install did not place your sign-in on the identity plane, so your workspaces and Create a workspace cannot be shown here.";

/** Placement is what Choose a workspace makes of where the sign-in landed. */
export type Placement<P> =
  | { kind: "plane"; plane: P }
  | { kind: "direct"; memberships: Membership[] }
  | { kind: "refused"; code: "no-plane"; message: string };

/**
 * placementOf reads where the sign-in landed: on the plane, whose verb lists
 * the memberships; placed directly in its one workspace, which is the one
 * membership — only where the plane was not asked for; or refused, with why.
 */
export function placementOf<P>(
  asked: boolean,
  who: { plane?: P | undefined; role?: Membership["role"] | undefined },
  direct: string | undefined,
): Placement<P> {
  if (who.plane !== undefined) {
    return { kind: "plane", plane: who.plane };
  }
  if (asked) {
    return { kind: "refused", code: "no-plane", message: PLANE_MISSED };
  }
  if (direct !== undefined) {
    return {
      kind: "direct",
      memberships: who.role === undefined ? [] : [{ tenant: direct, role: who.role }],
    };
  }
  return {
    kind: "refused",
    code: "no-plane",
    message: "the sign-in named no workspace and was placed in none",
  };
}

/**
 * skipsChoose is the one membership the page enters at once, with no page in
 * between, or undefined where the page stands: the person asked for it, or
 * there are none or several.
 */
export function skipsChoose(asked: boolean, memberships: readonly Membership[]): Membership | undefined {
  return !asked && memberships.length === 1 ? memberships[0] : undefined;
}
